import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit as fbLimit,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  updateDoc,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Customer, Order } from '@/types/schemas';

export const CUSTOMERS_PAGE_SIZE = 50;

export type CustomersPage = {
  customers: Customer[];
  cursor: QueryDocumentSnapshot<DocumentData> | null;
};

/** Full unpaginated list — only for search-across-everyone UIs (e.g. the
 * quotation builder's customer picker). Prefer fetchCustomersPage for
 * browsing lists. */
export async function fetchAllCustomers(): Promise<Customer[]> {
  const snap = await getDocs(query(collection(db, 'customers'), orderBy('name')));
  return snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<Customer, 'id'>),
  }));
}

export async function fetchCustomersPage(
  cursor: QueryDocumentSnapshot<DocumentData> | null = null,
): Promise<CustomersPage> {
  const constraints = cursor
    ? [orderBy('name'), startAfter(cursor), fbLimit(CUSTOMERS_PAGE_SIZE)]
    : [orderBy('name'), fbLimit(CUSTOMERS_PAGE_SIZE)];
  const snap = await getDocs(query(collection(db, 'customers'), ...constraints));
  const customers = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Customer, 'id'>) }));
  const nextCursor =
    snap.docs.length === CUSTOMERS_PAGE_SIZE ? snap.docs[snap.docs.length - 1] : null;
  return { customers, cursor: nextCursor };
}

export async function findCustomerByPhone(phone: string): Promise<Customer | null> {
  const trimmed = phone.trim();
  if (!trimmed) return null;
  const snap = await getDocs(
    query(collection(db, 'customers'), where('phone', '==', trimmed), fbLimit(1)),
  );
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...(d.data() as Omit<Customer, 'id'>) };
}

export async function fetchCustomer(id: string): Promise<Customer | null> {
  const snap = await getDoc(doc(db, 'customers', id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<Customer, 'id'>) };
}

export async function createCustomer(
  data: Omit<Customer, 'id' | 'totalLifetimeValue' | 'orderCount' | 'createdAt'>,
): Promise<string> {
  const ref = await addDoc(collection(db, 'customers'), {
    ...data,
    totalLifetimeValue: 0,
    orderCount: 0,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateCustomer(
  id: string,
  data: Partial<Customer>,
): Promise<void> {
  await updateDoc(doc(db, 'customers', id), { ...data, updatedAt: serverTimestamp() });
}

export async function deleteCustomer(id: string): Promise<void> {
  await deleteDoc(doc(db, 'customers', id));
}

export async function fetchCustomerOrders(customerId: string): Promise<Order[]> {
  const snap = await getDocs(
    query(
      collection(db, 'orders'),
      where('customerId', '==', customerId),
      orderBy('createdAt', 'desc'),
    ),
  );
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Order, 'id'>) }));
}
